import React from 'react';
import { twJoin } from 'tailwind-merge';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { AttributeTileDetail } from '../types/attribute-tile-detail';
import { PickerVariant } from '../types/picker-variant';
import { PickerSize } from '../types/picker-size';
import { AttributeTileOption } from '../attribute-tile-option/attribute-tile-option';

export interface AttributeTilePickerProps {
  sectionHeading?: RichTextFragment;
  options: AttributeTileDetail[];
  selected?: AttributeTileDetail;
  onSelect: (option: AttributeTileDetail) => void;
  variant?: PickerVariant;
  size?: PickerSize;
}

/**
 * Generic tile picker used by the accessory chooser for both the cylinder and
 * the flexible "attribute wildcard" selections. Behaves exactly like the Smart
 * Lock cylinder picker: image + bold label tiles. The whole section is hidden
 * when there are no options, and the heading is omitted when it is empty.
 */
export function AttributeTilePicker({
  sectionHeading,
  options,
  selected,
  onSelect,
  variant = 'default',
  size = 'default',
}: Readonly<AttributeTilePickerProps>) {
  const isClassic = variant === 'classic';

  if (!options?.length) {
    return null;
  }

  return (
    <div>
      {sectionHeading && (
        <RichTextRenderer
          className="text-xl"
          blocks={sectionHeading}
          mutedTextClassName={isClassic ? 'text-neutral-55' : undefined}
        />
      )}
      <div className={twJoin('mt-3 flex', size === 'small' ? 'gap-2' : 'gap-4')}>
        {options.map((option) => (
          <AttributeTileOption
            key={option.type}
            option={option}
            isSelected={selected?.type === option.type}
            onSelect={() => onSelect(option)}
            variant={variant}
            size={size}
          />
        ))}
      </div>
    </div>
  );
}
